import dayjs from "@eisox/dayjs";
import type { DateRange } from "@eisox/design-system";
import { z } from "@eisox/zod";

import { DATE_FORMAT } from "~/constants/date.constant";

const dayRangeSchema = z.object({ from: z.iso.date(), to: z.iso.date() }).refine(({ from, to }) => from <= to);

type DayRange = z.infer<typeof dayRangeSchema>;

interface InstantRange {
  startDate: string;
  endDate: string;
}

const todayIn = (timezone: string): string => dayjs().tz(timezone).format(DATE_FORMAT);

const toDateRange = (range: DayRange | undefined): DateRange => ({
  from: range ? dayjs(range.from) : null,
  to: range ? dayjs(range.to) : null,
});

const toDayRange = ({ from, to }: DateRange): DayRange | undefined =>
  from && to && !from.isAfter(to) ? { from: from.format(DATE_FORMAT), to: to.format(DATE_FORMAT) } : undefined;

// The last instant is taken from the next midnight: `endOf("day")` keeps the offset the day started
// with, which is one hour off on a daylight-saving change.
const toInstantRange = ({ from, to }: DayRange, timezone: string): InstantRange => ({
  startDate: dayjs.tz(from, timezone).toISOString(),
  endDate: dayjs.tz(dayjs(to).add(1, "day").format(DATE_FORMAT), timezone).subtract(1, "millisecond").toISOString(),
});

const toDayRangeIn = ({ startDate, endDate }: InstantRange, timezone: string): DayRange => ({
  from: dayjs(startDate).tz(timezone).format(DATE_FORMAT),
  to: dayjs(endDate).tz(timezone).format(DATE_FORMAT),
});

export { dayRangeSchema, toDateRange, toDayRange, toDayRangeIn, todayIn, toInstantRange };
export type { DayRange, InstantRange };
